import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ConsultationPricing.module.scss';

interface ConsultationPricingProps {
    form: any;
    setFieldValue: (field: string, value: any) => void;
}

const DURATION_OPTIONS = Array.from({ length: 36 }, (_, i) => (i + 1) * 5); // 5 to 180 in steps of 5

export const ConsultationPricing: React.FC<ConsultationPricingProps> = ({ form, setFieldValue }) => {
    const { t } = useTranslation();
    const details = form.details || {};
    const pricingMode = details.pricing_mode || 'single';
    const currency = details.currency || 'UYU';

    const [currencyMenuOpen, setCurrencyMenuOpen] = useState<'single' | 'multi' | null>(null);
    const [durationMenuOpen, setDurationMenuOpen] = useState<'single' | 'initial' | 'followup' | null>(null);

    const currencyRefSingle = useRef<HTMLDivElement>(null);
    const currencyRefSplit = useRef<HTMLDivElement>(null);
    const durationRefSingle = useRef<HTMLDivElement>(null);
    const durationRefInitial = useRef<HTMLDivElement>(null);
    const durationRefFollowup = useRef<HTMLDivElement>(null);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (currencyMenuOpen === 'single' && currencyRefSingle.current && !currencyRefSingle.current.contains(event.target as Node)) {
                setCurrencyMenuOpen(null);
            }
            if (currencyMenuOpen === 'multi' && currencyRefSplit.current && !currencyRefSplit.current.contains(event.target as Node)) {
                setCurrencyMenuOpen(null);
            }
            if (durationMenuOpen === 'single' && durationRefSingle.current && !durationRefSingle.current.contains(event.target as Node)) {
                setDurationMenuOpen(null);
            }
            if (durationMenuOpen === 'initial' && durationRefInitial.current && !durationRefInitial.current.contains(event.target as Node)) {
                setDurationMenuOpen(null);
            }
            if (durationMenuOpen === 'followup' && durationRefFollowup.current && !durationRefFollowup.current.contains(event.target as Node)) {
                setDurationMenuOpen(null);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setCurrencyMenuOpen(null);
                setDurationMenuOpen(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [currencyMenuOpen, durationMenuOpen]);

    const handleModeChange = (mode: 'single' | 'multi') => {
        setFieldValue('details.pricing_mode', mode);
    };

    const handleCurrencyChange = (newCurrency: 'UYU' | 'USD') => {
        setFieldValue('details.currency', newCurrency);
        setCurrencyMenuOpen(null);
    };

    const moneyPrefix = currency === 'USD' ? 'US$' : '$U';

    const formatDisplayPrice = (val: any) => {
        if (!val && val !== 0) return '';
        const numStr = String(val).replace(/\D/g, '');
        if (!numStr) return '';
        return Number(numStr).toLocaleString('es-UY');
    };

    const handlePriceChange = (field: string, val: string) => {
        const cleanVal = val.replace(/\D/g, '');
        setFieldValue(field, cleanVal ? Number(cleanVal) : '');
    };

    return (
        <div className={styles.consultationPricing}>
            <div className={styles.consultationPricingHead}>
                <div>
                    <div className={styles.label}>{t('EditProfileProfessional.consultationPricing.label', 'Valores de consulta')}</div>
                    <p className={styles.consultationPricingHelper}>
                        {t('EditProfileProfessional.consultationPricing.helper', 'Podés mantener un único valor o diferenciar la consulta inicial de las consultas de seguimiento. La duración interviene en la disponibilidad y la moneda puede definirse en UYU o USD.')}
                    </p>
                </div>
            </div>

            <div className={styles.pricingMode} role="radiogroup" aria-label={t('EditProfileProfessional.consultationPricing.modeAria', 'Esquema de valores de consulta')}>
                <label className={`${styles.pricingModeOption} ${pricingMode === 'single' ? styles.isSelected : ''}`}>
                    <input 
                        type="radio" 
                        name="pricing-mode" 
                        value="single" 
                        checked={pricingMode === 'single'} 
                        onChange={() => handleModeChange('single')} 
                    />
                    <span>{t('EditProfileProfessional.consultationPricing.singleValue', 'Un único valor')}</span>
                </label>
                <label className={`${styles.pricingModeOption} ${pricingMode === 'multi' ? styles.isSelected : ''}`}>
                    <input 
                        type="radio" 
                        name="pricing-mode" 
                        value="multi" 
                        checked={pricingMode === 'multi'} 
                        onChange={() => handleModeChange('multi')} 
                    />
                    <span>{t('EditProfileProfessional.consultationPricing.multiValue', 'Inicial + seguimiento')}</span>
                </label>
            </div>

            {pricingMode === 'single' && (
                <div className={styles.pricingPanel}>
                    <div className={styles.singlePricingRow}>
                        <div className={styles.pricingMiniField}>
                            <div className={styles.pricingMiniLabel}>{t('EditProfileProfessional.consultationPricing.valueLabel', 'Valor')}</div>
                            <div className={styles.pricingValue}>
                                <span style={{ marginRight: 8 }}>{moneyPrefix}</span>
                                <input 
                                    type="text" 
                                    value={details.consult_price === 0 || details.consult_price === '0' ? '' : formatDisplayPrice(details.consult_price)}
                                    onChange={(e) => handlePriceChange('details.consult_price', e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div className={styles.pricingMiniField}>
                            <div className={styles.pricingMiniLabel}>{t('EditProfileProfessional.consultationPricing.currencyLabel', 'Moneda')}</div>
                            <div className={styles.pricingCurrencyPicker} ref={currencyRefSingle}>
                                <button 
                                    className={styles.pricingCurrency} 
                                    type="button" 
                                    aria-expanded={currencyMenuOpen === 'single'}
                                    onClick={() => setCurrencyMenuOpen(currencyMenuOpen === 'single' ? null : 'single')}
                                >
                                    <span>{currency}</span><span className={styles.pricingCaret}>▾</span>
                                </button>
                                {currencyMenuOpen === 'single' && (
                                    <div className={styles.pricingCurrencyMenu}>
                                        <div className={styles.pricingCurrencyOptions} role="listbox">
                                            <button 
                                                className={`${styles.pricingCurrencyOption} ${currency === 'UYU' ? styles.isActive : ''}`} 
                                                onClick={() => handleCurrencyChange('UYU')}
                                                type="button"
                                            >
                                                <strong>UYU</strong><span>{t('EditProfileProfessional.consultationPricing.currencyUyu', 'Peso uruguayo')}</span>
                                            </button>
                                            <button 
                                                className={`${styles.pricingCurrencyOption} ${currency === 'USD' ? styles.isActive : ''}`} 
                                                onClick={() => handleCurrencyChange('USD')}
                                                type="button"
                                            >
                                                <strong>USD</strong><span>{t('EditProfileProfessional.consultationPricing.currencyUsd', 'Dólar estadounidense')}</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.pricingMiniField}>
                            <div className={styles.pricingMiniLabel}>{t('EditProfileProfessional.consultationPricing.durationLabel', 'Duración')}</div>
                            <div className={styles.pricingDurationPicker} ref={durationRefSingle}>
                                <button 
                                    className={styles.pricingDuration} 
                                    type="button"
                                    aria-expanded={durationMenuOpen === 'single'}
                                    onClick={() => setDurationMenuOpen(durationMenuOpen === 'single' ? null : 'single')}
                                >
                                    <span>{details.consult_time || 60} min</span><span className={styles.pricingCaret}>▾</span>
                                </button>
                                {durationMenuOpen === 'single' && (
                                    <div className={styles.pricingDurationMenu}>
                                        <div className={styles.pricingDurationOptions} role="listbox">
                                            {DURATION_OPTIONS.map(min => (
                                                <button
                                                    key={min}
                                                    type="button"
                                                    className={`${styles.pricingDurationOption} ${Number(details.consult_time) === min ? styles.isActive : ''}`}
                                                    onClick={() => {
                                                        setFieldValue('details.consult_time', min);
                                                        setDurationMenuOpen(null);
                                                    }}
                                                >
                                                    {min} min
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {pricingMode === 'multi' && (
                <div className={styles.pricingPanel}>
                    <div className={styles.pricingSplitShell}>
                        <div className={styles.consultationTypes}>
                            <article className={styles.consultationTypeCard}>
                                <div className={styles.consultationTypeHead}>
                                    <div className={styles.consultationTypeCopy}>
                                        <h4 className={styles.consultationTypeTitle}>{t('EditProfileProfessional.consultationPricing.initialConsultTitle', 'Consulta inicial')}</h4>
                                        <p className={styles.consultationTypeDesc}>{t('EditProfileProfessional.consultationPricing.initialConsultDesc', 'Para la primera entrevista, evaluación o abordaje inicial.')}</p>
                                    </div>
                                    <span className={styles.consultationTypeBadge}>{t('EditProfileProfessional.consultationPricing.initialBadge', 'Inicial')}</span>
                                </div>
                                <div className={styles.pricingFields}>
                                    <div className={styles.pricingMiniField}>
                                        <div className={styles.pricingMiniLabel}>{t('EditProfileProfessional.consultationPricing.valueLabel', 'Valor')}</div>
                                        <div className={styles.pricingValueCombo}>
                                            <div className={styles.pricingValue}>
                                                <span style={{ marginRight: 8 }}>{moneyPrefix}</span>
                                                <input 
                                                    type="text" 
                                                    value={details.initial_price === 0 || details.initial_price === '0' ? '' : formatDisplayPrice(details.initial_price)}
                                                    onChange={(e) => handlePriceChange('details.initial_price', e.target.value)}
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className={styles.pricingCurrencyPicker} ref={currencyRefSplit}>
                                                <button 
                                                    className={styles.pricingCurrency} 
                                                    type="button"
                                                    aria-expanded={currencyMenuOpen === 'multi'}
                                                    onClick={() => setCurrencyMenuOpen(currencyMenuOpen === 'multi' ? null : 'multi')}
                                                >
                                                    <span>{currency}</span><span className={styles.pricingCaret}>▾</span>
                                                </button>
                                                {currencyMenuOpen === 'multi' && (
                                                    <div className={styles.pricingCurrencyMenu}>
                                                        <div className={styles.pricingCurrencyOptions} role="listbox">
                                                            <button 
                                                                className={`${styles.pricingCurrencyOption} ${currency === 'UYU' ? styles.isActive : ''}`} 
                                                                onClick={() => handleCurrencyChange('UYU')}
                                                                type="button"
                                                            >
                                                                <strong>UYU</strong><span>{t('EditProfileProfessional.consultationPricing.currencyUyu', 'Peso uruguayo')}</span>
                                                            </button>
                                                            <button 
                                                                className={`${styles.pricingCurrencyOption} ${currency === 'USD' ? styles.isActive : ''}`} 
                                                                onClick={() => handleCurrencyChange('USD')}
                                                                type="button"
                                                            >
                                                                <strong>USD</strong><span>{t('EditProfileProfessional.consultationPricing.currencyUsd', 'Dólar estadounidense')}</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.pricingMiniField}>
                                        <div className={styles.pricingMiniLabel}>{t('EditProfileProfessional.consultationPricing.durationLabel', 'Duración')}</div>
                                        <div className={styles.pricingDurationPicker} ref={durationRefInitial}>
                                            <button 
                                                className={styles.pricingDuration} 
                                                type="button"
                                                aria-expanded={durationMenuOpen === 'initial'}
                                                onClick={() => setDurationMenuOpen(durationMenuOpen === 'initial' ? null : 'initial')}
                                            >
                                                <span>{details.initial_duration || 90} min</span><span className={styles.pricingCaret}>▾</span>
                                            </button>
                                            {durationMenuOpen === 'initial' && (
                                                <div className={styles.pricingDurationMenu}>
                                                    <div className={styles.pricingDurationOptions} role="listbox">
                                                        {DURATION_OPTIONS.map(min => (
                                                            <button
                                                                key={min}
                                                                type="button"
                                                                className={`${styles.pricingDurationOption} ${Number(details.initial_duration) === min ? styles.isActive : ''}`}
                                                                onClick={() => {
                                                                    setFieldValue('details.initial_duration', min);
                                                                    setDurationMenuOpen(null);
                                                                }}
                                                            >
                                                                {min} min
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>

                            <article className={styles.consultationTypeCard}>
                                <div className={styles.consultationTypeHead}>
                                    <div className={styles.consultationTypeCopy}>
                                        <h4 className={styles.consultationTypeTitle}>{t('EditProfileProfessional.consultationPricing.followupConsultTitle', 'Consulta de seguimiento')}</h4>
                                        <p className={styles.consultationTypeDesc}>{t('EditProfileProfessional.consultationPricing.followupConsultDesc', 'Para encuentros posteriores a la consulta inicial.')}</p>
                                    </div>
                                    <span className={styles.consultationTypeBadge}>{t('EditProfileProfessional.consultationPricing.followupBadge', 'Seguimiento')}</span>
                                </div>
                                <div className={styles.pricingFields}>
                                    <div className={styles.pricingMiniField}>
                                        <div className={styles.pricingMiniLabel}>{t('EditProfileProfessional.consultationPricing.valueLabel', 'Valor')}</div>
                                        <div className={styles.pricingValue}>
                                            <span style={{ marginRight: 8 }}>{moneyPrefix}</span>
                                            <input 
                                                type="text" 
                                                value={details.followup_price === 0 || details.followup_price === '0' ? '' : formatDisplayPrice(details.followup_price)}
                                                onChange={(e) => handlePriceChange('details.followup_price', e.target.value)}
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.pricingMiniField}>
                                        <div className={styles.pricingMiniLabel}>{t('EditProfileProfessional.consultationPricing.durationLabel', 'Duración')}</div>
                                        <div className={styles.pricingDurationPicker} ref={durationRefFollowup}>
                                            <button 
                                                className={styles.pricingDuration} 
                                                type="button"
                                                aria-expanded={durationMenuOpen === 'followup'}
                                                onClick={() => setDurationMenuOpen(durationMenuOpen === 'followup' ? null : 'followup')}
                                            >
                                                <span>{details.followup_duration || 30} min</span><span className={styles.pricingCaret}>▾</span>
                                            </button>
                                            {durationMenuOpen === 'followup' && (
                                                <div className={styles.pricingDurationMenu}>
                                                    <div className={styles.pricingDurationOptions} role="listbox">
                                                        {DURATION_OPTIONS.map(min => (
                                                            <button
                                                                key={min}
                                                                type="button"
                                                                className={`${styles.pricingDurationOption} ${Number(details.followup_duration) === min ? styles.isActive : ''}`}
                                                                onClick={() => {
                                                                    setFieldValue('details.followup_duration', min);
                                                                    setDurationMenuOpen(null);
                                                                }}
                                                            >
                                                                {min} min
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        </div>
                    </div>
                    <p className={styles.pricingNote}>
                        <span className={styles.pricingNoteMark}>i</span>
                        <span>{t('EditProfileProfessional.consultationPricing.note', 'La moneda se selecciona una sola vez en Consulta inicial y se aplica también al seguimiento. UYU se muestra como $U y USD como US$. Cambiar de moneda no convierte automáticamente los importes.')}</span>
                    </p>
                </div>
            )}
        </div>
    );
};
